import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const coverage = [
  {
    ticker: "DLB",
    company: "Dolby Laboratories",
    sector: "Technology",
    thesis:
      "Dolby's licensing model is structurally advantaged — every device shipping with Dolby Atmos or Vision pays a royalty. The risk is cinema cyclicality and whether spatial audio on mobile platforms sustains premium pricing. Long-term, the moat is the standard itself.",
    research: [
      { date: "2026-07-10", tag: "Q3 '26 Preview", body: "Expecting licensing revenue to reaccelerate as device shipment cycle normalizes. Cinema segment still a drag. Watch for commentary on auto licensing pipeline." },
    ],
    model: {
      metrics: [
        { label: "Revenue (TTM)", value: "$1.42B" },
        { label: "Licensing mix", value: "~88%" },
        { label: "Gross margin", value: "89.2%" },
        { label: "FCF margin", value: "~28%" },
        { label: "P/E (fwd)", value: "~28x" },
      ],
      assumptions: "Licensing grows mid-single-digits on device volume recovery. Cinema flat to down. Operating leverage drives ~100bps margin expansion annually.",
    },
  },
  {
    ticker: "SONY",
    company: "Sony Group",
    sector: "Conglomerate",
    thesis:
      "Sony is an IP compounding machine across gaming (PS5 + PS Plus), music (catalog + streaming), and image sensors. The sum-of-parts story is real but the market treats it as a hardware company. Key risk: gaming cycle troughs and semiconductor capex timing.",
    research: [
      { date: "2026-06-15", tag: "FY26 Guidance", body: "Management guiding conservative on gaming but music and pictures both strong. Sensor segment recovery tied to smartphone cycle — second half weighted." },
    ],
    model: {
      metrics: [
        { label: "Revenue (FY26E)", value: "¥13.0T" },
        { label: "Gaming rev share", value: "~28%" },
        { label: "Music rev share", value: "~15%" },
        { label: "Op margin", value: "~12%" },
        { label: "P/E (fwd)", value: "~17x" },
      ],
      assumptions: "Music segment grows high-single-digits on streaming + catalog monetization. Gaming normalizes post-cycle. Sensors recover in H2 with new phone launches.",
    },
  },
  {
    ticker: "BBY",
    company: "Best Buy",
    sector: "Consumer Retail",
    thesis:
      "Best Buy is the last standing specialty electronics retailer at scale. The bull case is services attach (Totaltech, installation) driving recurring revenue as hardware commoditizes. The bear case is secular traffic decline and Amazon share gains.",
    research: [
      { date: "2026-05-20", tag: "Q1 '27 Earnings", body: "Comp sales slightly negative but better than feared. Services revenue up 8% YoY. Inventory clean. Guidance maintained — management sounds cautiously optimistic on back-to-school." },
    ],
    model: {
      metrics: [
        { label: "Revenue (TTM)", value: "$41.2B" },
        { label: "Comp sales", value: "-1.8%" },
        { label: "Gross margin", value: "23.1%" },
        { label: "Services mix", value: "~7%" },
        { label: "Div yield", value: "~4.5%" },
      ],
      assumptions: "Comps stabilize near flat. Services mix expands 50-100bps/yr. Margin steady with cost discipline. Capital return story (buyback + dividend) is the floor.",
    },
  },
  {
    ticker: "SBUX",
    company: "Starbucks",
    sector: "Consumer Staples",
    thesis:
      "Starbucks is repricing to a premium convenience brand. The new CEO's 'Back to Starbucks' strategy is simplifying the menu and improving throughput. China remains the swing factor — recovery there changes the multiple. US same-store traffic is the KPI.",
    research: [
      { date: "2026-06-28", tag: "Strategy Update", body: "New store format (smaller, faster) rolling out in 200 locations. Early throughput data encouraging. China JV structure under review — potential for capital-light model." },
    ],
    model: {
      metrics: [
        { label: "Revenue (TTM)", value: "$36.8B" },
        { label: "US SSS", value: "+2.1%" },
        { label: "China SSS", value: "-4.3%" },
        { label: "Op margin", value: "~15%" },
        { label: "P/E (fwd)", value: "~24x" },
      ],
      assumptions: "US comps +2-3% on traffic recovery + pricing. China stabilizes by mid-FY27. Margin recovery to 17%+ over 2 years as throughput improvements scale.",
    },
  },
  {
    ticker: "FRCOY",
    company: "Fast Retailing",
    sector: "Consumer Discretionary",
    thesis:
      "Uniqlo's global expansion runway is long — underpenetrated in Europe and US relative to Zara/H&M. The model is high-quality basics at scale with tight inventory management. Risk is FX (yen weakness helps, strength hurts) and execution in new markets.",
    research: [
      { date: "2026-07-01", tag: "H1 FY26 Review", body: "International Uniqlo grew 18% in constant currency. Europe outperforming plan. US still subscale but improving. GU brand dragging — may need strategic review." },
    ],
    model: {
      metrics: [
        { label: "Revenue (FY26E)", value: "¥3.2T" },
        { label: "Intl Uniqlo share", value: "~52%" },
        { label: "Gross margin", value: "~52%" },
        { label: "Op margin", value: "~14%" },
        { label: "P/E (fwd)", value: "~32x" },
      ],
      assumptions: "International Uniqlo grows mid-teens on store openings + SSS. Japan flat. GU restructuring adds 50bps to consolidated margin by FY28.",
    },
  },
  {
    ticker: "NFLX",
    company: "Netflix",
    sector: "Communication Services",
    thesis:
      "Netflix has won the streaming wars — the question now is how much operating leverage the model has. Ad tier is the next growth vector. Content spend efficiency (hit rate per dollar) is improving. The bear case is saturation + churn in mature markets.",
    research: [
      { date: "2026-06-22", tag: "Q2 '26 Preview", body: "Ad tier now 45M+ MAUs globally. ARPU still well below linear TV but gap closing. Content slate for H2 is strong. Expect FX to be a 2-3pt headwind on revenue." },
    ],
    model: {
      metrics: [
        { label: "Revenue (TTM)", value: "$42.1B" },
        { label: "Subscribers", value: "~295M" },
        { label: "Ad tier MAUs", value: "~45M" },
        { label: "Op margin", value: "~28%" },
        { label: "P/E (fwd)", value: "~35x" },
      ],
      assumptions: "Revenue grows low-teens on ARPU + ad tier scaling. Content spend flat as % of revenue. Margin expands 200-300bps/yr toward 32%+ by 2028.",
    },
  },
];

const TABS = ["Thesis", "Research", "Model"];
const PASSWORD = "5711";

function DetailPanel({ company: c }) {
  const [tab, setTab] = useState("Thesis");

  return (
    <motion.div
      key={c.ticker}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <div className="mb-8">
        <div className="flex items-baseline gap-3 mb-1">
          <span className="font-mono text-[20px] font-bold tracking-tight text-[#0a1628]">
            {c.ticker}
          </span>
          <span className="font-sans text-[14px] text-[#6b7280]">
            {c.sector}
          </span>
        </div>
        <h2 className="font-sans text-[15px] text-[#374151]">
          {c.company}
        </h2>
      </div>

      <div className="flex gap-0 mb-8 border-b border-[#e5e7eb]">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 pb-2.5 font-mono text-[11px] font-medium tracking-[0.05em] uppercase transition-colors relative ${
              tab === t
                ? "text-[#0a1628]"
                : "text-[#9ca3af] hover:text-[#4b5563]"
            }`}
          >
            {t}
            {tab === t && (
              <motion.span
                layoutId="er-tab"
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0a1628]"
                transition={{ duration: 0.2 }}
              />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab === "Thesis" && (
          <motion.div key="thesis" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <p className="font-sans text-[14px] leading-[1.85] text-[#374151] max-w-[48rem]">
              {c.thesis}
            </p>
          </motion.div>
        )}

        {tab === "Research" && (
          <motion.div key="research" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="space-y-5">
            {c.research.map((entry, i) => (
              <div key={i} className="border-l-2 border-[#d1d5db] pl-5">
                <div className="flex items-baseline gap-3 mb-1.5">
                  <span className="font-mono text-[12px] text-[#6b7280] tabular-nums">
                    {entry.date}
                  </span>
                  <span className="font-mono text-[10px] font-medium text-[#0a1628] bg-[#f3f4f6] px-2 py-0.5 rounded tracking-[0.04em] uppercase">
                    {entry.tag}
                  </span>
                </div>
                <p className="font-sans text-[13.5px] leading-[1.75] text-[#4b5563] max-w-[48rem]">
                  {entry.body}
                </p>
              </div>
            ))}
          </motion.div>
        )}

        {tab === "Model" && (
          <motion.div key="model" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="space-y-8">
            <div className="bg-[#f9fafb] border border-[#e5e7eb] rounded-lg overflow-hidden">
              <table className="w-full max-w-[36rem]">
                <tbody>
                  {c.model.metrics.map((m, i) => (
                    <tr key={m.label} className={i < c.model.metrics.length - 1 ? "border-b border-[#e5e7eb]" : ""}>
                      <td className="py-3 px-4 font-sans text-[13px] text-[#6b7280]">
                        {m.label}
                      </td>
                      <td className="py-3 px-4 font-mono text-[14px] font-medium text-[#0a1628] tabular-nums text-right">
                        {m.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <h4 className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af] mb-2">
                Key Assumptions
              </h4>
              <p className="font-sans text-[13.5px] leading-[1.75] text-[#4b5563] max-w-[48rem]">
                {c.model.assumptions}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function EquityResearch() {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(coverage[0].ticker);

  const activeCompany = coverage.find((c) => c.ticker === selected) || coverage[0];

  useEffect(() => {
    document.title = "Equity Research Portal";
    return () => { document.title = "Murat Gulcelik"; };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pw === PASSWORD) {
      setAuthed(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm px-6"
        >
          <div className="bg-white border border-[#e5e7eb] rounded-xl p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-[13px] font-bold text-[#0a1628]">ERP</span>
              <span className="w-[1px] h-3 bg-[#d1d5db]" />
              <span className="font-sans text-[13px] text-[#6b7280]">Equity Research</span>
            </div>
            <p className="font-sans text-[12px] text-[#9ca3af] mb-6">
              Password required
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="password"
                value={pw}
                onChange={(e) => { setPw(e.target.value); setError(false); }}
                placeholder="Enter password"
                autoFocus
                className="w-full bg-[#f9fafb] border border-[#e5e7eb] rounded-lg px-3.5 py-2.5 font-mono text-[13px] text-[#0a1628] placeholder:text-[#c4c8cf] focus:border-[#6b7280] focus:outline-none transition-colors"
              />
              {error && (
                <p className="font-mono text-[11px] text-[#ef4444]">
                  Invalid credentials
                </p>
              )}
              <button
                type="submit"
                className="w-full bg-[#0a1628] text-white font-mono text-[12px] font-medium tracking-[0.02em] py-2.5 rounded-lg hover:bg-[#1a2640] transition-colors"
              >
                Authenticate
              </button>
            </form>
          </div>
          <Link
            to="/"
            className="inline-block mt-6 font-sans text-[12px] text-[#9ca3af] hover:text-[#4b5563] transition-colors"
          >
            ← muratgulcelik.com
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb] text-[#0a1628]">
      <header className="border-b border-[#e5e7eb] sticky top-0 z-10 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 h-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[13px] font-bold tracking-tight text-[#0a1628]">ERP</span>
            <span className="w-[1px] h-3 bg-[#d1d5db]" />
            <span className="font-sans text-[12px] text-[#9ca3af]">Coverage Universe</span>
          </div>
          <Link
            to="/"
            className="font-sans text-[11px] text-[#9ca3af] hover:text-[#4b5563] transition-colors"
          >
            ← Main site
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-8 py-8">
        <div className="flex gap-2 mb-10 overflow-x-auto pb-1">
          {coverage.map((c) => (
            <button
              key={c.ticker}
              type="button"
              onClick={() => setSelected(c.ticker)}
              className={`flex-shrink-0 px-4 py-2 font-mono text-[12px] font-semibold tracking-[0.02em] transition-all duration-150 border rounded-md ${
                selected === c.ticker
                  ? "bg-[#0a1628] text-white border-[#0a1628]"
                  : "bg-white text-[#6b7280] border-[#e5e7eb] hover:border-[#9ca3af] hover:text-[#0a1628]"
              }`}
            >
              {c.ticker}
            </button>
          ))}
        </div>

        <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 sm:p-8">
          <DetailPanel company={activeCompany} />
        </div>
      </main>
    </div>
  );
}
